import React, { useState, useImperativeHandle, useEffect } from 'react'
import { useFormik, FormikProvider, FieldArray } from 'formik'

// queries
import { GET_ALL_EQUIPMENT_FOR_SELECT_FIELD } from '../../../../../graphql/equipment/equipment.queries'
import { GET_ALL_JOBS_FOR_SELECT_FIELD } from '../../../../../graphql/job/job.queries'
import { GET_ALL_TRUCKS_FOR_SELECT_FIELD } from '../../../../../graphql/truck/truck.queries'

// components
import FormSectionHeader from '../../../../../components/forms/FormSectionHeader/FormSectionHeader.component'
import AsyncSelectField from '../../../../../components/forms/AsyncSelectField/AsyncSelectField.component'
import ActionButton from '../../../../../components/buttons/ActionButton/ActionButton.component'
import EquipmentRequestInput from '../../../../../components/forms/lists/EquipmentRequestInput/EquipmentRequestInput.component'
import InputField from '../../../../../components/forms/InputField/InputField.component'

// mui
import { Grid, Container, Typography, Alert } from '@mui/material'

// utils
import { calculateTotalWeight } from './EquipmentRequest.form.utils'

const EquipmentRequestForm = React.forwardRef(({ errors, id }, ref) => {
    const [selectedEquipment, setSelectedEquipment] = useState(null)
    const [selectedTruck, setSelectedTruck] = useState(null)

    useImperativeHandle(ref, () => ({
        getData: () => { return formik.values }
    }))

    const formik = useFormik({
        initialValues: {
            job: id ? id : '',
            truck: '',
            notes: '',
            equipment: [],
            equipmentSearch: '',
        },
        onSubmit: values => {
            alert(JSON.stringify(values, null, 2))
        }
    })

    const totalWeight = calculateTotalWeight(formik.values.equipment)

    // Show server errors in the form if any
    const hasServerError = errors.__server

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                {/* Show server error at the top if exists */}
                {hasServerError && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {hasServerError}
                    </Alert>
                )}

                <Container disableGutters>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <AsyncSelectField 
                                id="job"
                                value={formik.values.job}
                                onChange={value => formik.setFieldValue('job', parseInt(value.value))}
                                placeholder="Select a Job"
                                query={{
                                    query: GET_ALL_JOBS_FOR_SELECT_FIELD,
                                    rules: {
                                        loopField: 'allJobsLite',
                                        label: ['name'],
                                        value: 'id'
                                    }
                                    
                                }}
                                isValid={errors['job']}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AsyncSelectField 
                                id="truck"
                                value={formik.values.truck}
                                onChange={value => {
                                    setSelectedTruck(value.data)
                                    formik.setFieldValue('truck', parseInt(value.value))
                                }}
                                placeholder="Select a Truck"
                                query={{
                                    query: GET_ALL_TRUCKS_FOR_SELECT_FIELD,
                                    rules: {
                                        loopField: 'allTrucks',
                                        label: ['number'],
                                        value: 'id'
                                    }
                                    
                                }}
                                isValid={errors['truck']}
                            />
                        </Grid>
                        <Grid item xs={12}>
                        <InputField  
                                id="notes"
                                type="textarea"
                                placeholder='Notes'
                                onChange={formik.handleChange}
                                value={formik.values.notes}
                                isValid={errors['notes']}
                                required={false}
                            />
                        </Grid>
                    </Grid>
                </Container>

                <FieldArray 
                    name='equipment'
                    render={({ push, remove }) => (
                        <div>
                            <FormSectionHeader text='Equipment' />
                                <Grid container spacing={2} alignItems='center'>
                                    <Grid item xs={10}>
                                        <AsyncSelectField 
                                            id="equipmentSearch"
                                            value={formik.values.equipmentSearch}
                                            placeholder="Search"
                                            onChange={value => {
                                                setSelectedEquipment(value.data)
                                                formik.setFieldValue('equipmentSearch', value.value)}}
                                            query={{
                                                query: GET_ALL_EQUIPMENT_FOR_SELECT_FIELD,
                                                rules: {
                                                    loopField: 'allEquipment',
                                                    label: ['sku', 'name'],
                                                    value: 'id',
                                                    groupBy: 'category.name'
                                                }
                                            }}
                                            grouped
                                            required={false}
                                        />
                                    </Grid>

                                    <Grid container item xs={2}>
                                        <ActionButton sx={{ width: "100%", marginBottom: 2 }} label='Add' disabled={selectedEquipment == null} onClick={(e) => {
                                            e.preventDefault();
                                            
                                            push({...selectedEquipment,id: parseInt(selectedEquipment.id), requestQty: ''})
                                        }} />
                                    </Grid>
                                </Grid>

                                <Grid container>
                                    <Grid item xs={6}><Typography>Weight:</Typography></Grid>
                                    <Grid item xs={6} textAlign='end'><Typography>{totalWeight} / {selectedTruck ? selectedTruck.maxLoadInLbs : "0"} lbs</Typography></Grid>
                                </Grid>

                            {
                                formik.values.equipment.map((item, index) => {
                                    return (<EquipmentRequestInput 
                                        key={index}
                                        prefix={`equipment.${index}`}
                                        handleChange={formik.handleChange}
                                        equipmentName={item.name}
                                        sku={item.sku}
                                        requestQty={item.requestQty}
                                        handleRemove={(e) => {
                                            e.preventDefault()
                                            remove(index)
                                        }}
                                        errors={errors['equipment'].length > index ? errors['equipment'][index] : {}}
                                    />)
                                })
                            }
                        </div>
                    )}
                />
            </form>
        </FormikProvider>
    )
})

export default EquipmentRequestForm